import Vue from 'vue'
import { mapActions, mapMutations, mapState } from 'vuex'
import archivableMixin from './archivableMixin'
import jobPartiesMixin from './jobPartiesMixin'
import startableMixin from './startableMixin'
import statusesMixin from './statusesMixin'
import waitForRefreshApplicationMixin from './waitForRefreshApplicationMixin'
import BigNumber from 'bignumber.js'
import { declineOffer } from '@/api/jobs/offers/offers'
import { JobOfferStages } from '@/constants/job/jobOfferStages'
import snackMixin from '@/mixins/snackMixin'
import { googleAnalyticsV2 } from '@/servicies-ts/analytics'
import { RootState } from '@/store'

export default Vue.extend({
  mixins: [archivableMixin, jobPartiesMixin, snackMixin, startableMixin, statusesMixin, waitForRefreshApplicationMixin],
  computed: {
    ...mapState<RootState>({
      application: (state: RootState) => state.jobChat.application.value,
      acceptingOffer: (state: RootState) => state.jobChat.acceptingOffer,
      decliningOffer: (state: RootState) => state.jobChat.decliningOffer,
    }),
    hasSendOffer() {
      return (
        this.isWorker &&
        !this.isArchived &&
        !this.isMetaArchived &&
        (!this.application?.offer ||
          [JobOfferStages.DECLINED_BY_CUSTOMER, JobOfferStages.DECLINED_BY_FREELANCER].includes(this.application.offer.stage))
      )
    },
    appOffer() {
      return this.application.offer
    },
    accepting() {
      return this.acceptingOffer && this.appOffer?.id === this.acceptingOffer
    },
    declining() {
      return this.decliningOffer && this.appOffer?.id === this.decliningOffer
    },
  },
  methods: {
    ...mapMutations({
      updateApplication: 'jobChat/updateApplication',
      setAcceptingOffer: 'jobChat/setAcceptingOffer',
      setDecliningOffer: 'jobChat/setDecliningOffer',
    }),
    ...mapActions({
      openModal: 'ui/openModal',
    }),
    sendOffer() {
      const budget = Number(this.application.budget) || 15

      this.openModal({
        component: 'lx-lazy-modal',
        props: {
          factory: import(/* webpackChunkName: "job-modals" */ '@/modals/SendOffer/SendOffer.vue'),
          title: 'Send an offer',
          props: {
            applicationId: this.application.id,
            initBudget: budget,
            initDays: this.application.deadlineInDays,
            onSuccess: this.waitRefreshApplication,
          },
        },
      })
    },
    async cancelOffer() {
      try {
        const id = this.appOffer.id
        this.setDecliningOffer(id)
        await declineOffer({ id: this.appOffer.id, comment: 'Not specified' })
        if (this.appOffer.id === id) {
          this.updateApplication({ id: this.application.id, offer: null })
        }
      } catch (e) {
        this.openSnackbar({
          type: this.SnackTypes.FAILURE,
          text: 'Error declining offer. Please try again.',
        })
      } finally {
        this.setDecliningOffer(null)
      }
    },
    async editOffer() {
      if (!this.appOffer) return

      this.openModal({
        component: 'lx-lazy-modal',
        props: {
          factory: import(/* webpackChunkName: "job-modals" */ '@/modals/SendOffer/SendOffer.vue'),
          title: 'Send an offer',
          props: {
            applicationId: this.application.id,
            offer: this.appOffer,
            initBudget: new BigNumber(this.appOffer.budget).toNumber(),
            initDays: this.appOffer.deadlineInDays,
            initHours: this.appOffer.timeValue,
            onSuccess: this.waitRefreshApplication,
          },
        },
      })
    },
    async acceptOffer() {
      this.setAcceptingOffer(this.appOffer.id)
      try {
        if (this.isMetaArchived) {
          await this.setArchivedMeta(false)
        }
        this.openModal({
          component: 'lx-lazy-modal',
          props: {
            factory: import(/* webpackChunkName: "job-modals" */ '@/modals/AcceptTalentsOffer/AcceptTalentsOffer.vue'),
            title: `Accept Talent's Offer`,
            props: {
              application: this.application,
              onSuccess: async () => {
                googleAnalyticsV2.send({
                  event: 'response-to-job',
                  'event-content': 'customer-button-click-hire-offer',
                  job_id: this.application.job.id,
                })
                googleAnalyticsV2.send({
                  event: 'job_accept_click',
                  job_id: this.application.job.id,
                })
                await this.waitRefreshApplication()
                this.startJob()
              },
            },
          },
        })
      } finally {
        this.setAcceptingOffer(null)
      }
    },
  },
})
